import { DateTime } from "luxon";
import {
  DatePickerType,
  EisenhowerPriority,
  SQDCActionStatus,
  SQDCEABShorthandDeadline,
  SQDCEABShorthandResponsible,
  SQDCEventActionTypeFieldType,
  SQDCEventTypeFieldRelation,
  type SQDCEABShorthandsQuery,
} from "@/graphql";
import { getCardsForRevision } from "@/modules/sqdc/components/eventActionBoard/EventActionHelper";
import store from "@/store";
import type { ModalActionEditType, ModalEventEditType, ModalEventType } from "./SQDCModelHelper";
import { normalizeIsoDateToGranularity } from "./SQDCDateGranularityHelper";

type SQDCShorthand = Pick<
  SQDCEABShorthandsQuery["SQDCEABShorthands"][number],
  | "responsible"
  | "responsibleId"
  | "responsibleFunctionId"
  | "deadlineType"
  | "deadlineValue"
  | "deadlineWeekday"
  | "deadlineDate"
  | "status"
  | "eisenhowerPriority"
  | "FieldValues"
> & {
  FlagOptions?: Array<{ flagId: number }>;
  ResponsibleFunction?: {
    Assignments: Array<{ siteId?: number; userId: number }>;
  };
};
type SQDCShorthandField = SQDCShorthand["FieldValues"][number]["Field"];
type ShorthandDateFieldDefault = {
  mode?: SQDCEABShorthandDeadline;
  value?: number;
  weekday?: number;
  date?: string;
};
export type ShorthandUserFieldDefault = {
  responsible: SQDCEABShorthandResponsible;
  responsibleId?: number;
  responsibleFunctionId?: number;
  userOptionIds?: number[];
  overlayResponsibleRole?: boolean;
};
export type ShorthandUserFunctionAssignments = Record<
  number,
  Array<{ siteId?: number; userId: number }>
>;

function hasValue(value: unknown) {
  return value != null && value !== "" && (!Array.isArray(value) || value.length > 0);
}

export function getModalEventTypeForLatestRevision(eventType: ModalEventType): ModalEventType {
  const cards = eventType.Cards ?? [];
  const fields = eventType.SQDCEventActionTypeFields ?? [];
  const maxRevision = Math.max(
    fields.reduce(
      (max, field) => Math.max(max, field.addedByRevision, field.removedByRevision ?? 0),
      1,
    ),
    cards.reduce(
      (max, card) => Math.max(max, card.validSinceRevision, card.removedByRevision ?? 0),
      1,
    ),
  );

  return {
    ...eventType,
    cards: getCardsForRevision(cards, maxRevision),
    SQDCEventActionTypeFields: fields.filter(
      (field) =>
        field.addedByRevision <= maxRevision &&
        (field.removedByRevision == null || field.removedByRevision > maxRevision),
    ),
  };
}

export function createDefaultEventFromShorthand(
  shorthand: SQDCShorthand,
  siteId?: number,
  userFunctionAssignments: ShorthandUserFunctionAssignments = {},
  lastWorkflowUserId?: number,
): ModalEventEditType {
  const defaultEvent: ModalEventEditType = {
    creatorId: getEventCreatorId(shorthand, siteId),
    occurrenceDate: normalizeIsoDateToGranularity(
      getDateFromShorthand(shorthand),
      DatePickerType.MINUTE,
    ),
    eisenhowerPriority: shorthand.eisenhowerPriority ?? EisenhowerPriority.NO_PRIORITY,
    flagIds: shorthand.FlagOptions?.map(({ flagId }) => flagId) ?? [],
    fileIds: [],
    SQDCEventTextFields: [],
    SQDCEventNumberFields: [],
    SQDCEventDateFields: [],
    SQDCEventBooleanFields: [],
    SQDCEventCostCenterFields: [],
    SQDCEventTechnicalPlaceFields: [],
    SQDCEventUserFields: [],
    SQDCEventIdeaCategoryFields: [],
    SQDCEventPercentSharingFields: [],
    SQDCEventOutageFields: [],
    SQDCEventErrorCatalogueFields: [],
    SQDCEventProductFamilyFields: [],
  };

  shorthand.FieldValues.forEach((fieldValue) => {
    setEventCustomFieldValue(
      defaultEvent,
      fieldValue.fieldId,
      fieldValue.Field,
      resolveUserFieldDefault(
        fieldValue.value,
        fieldValue.Field,
        siteId,
        userFunctionAssignments,
        lastWorkflowUserId,
      ),
    );
  });

  return defaultEvent;
}

export function createDefaultActionFromShorthand(
  shorthand: SQDCShorthand,
  eventOwnerId?: number,
  siteId?: number,
  userFunctionAssignments: ShorthandUserFunctionAssignments = {},
  lastWorkflowUserId?: number,
): ModalActionEditType {
  const defaultAction: ModalActionEditType = {
    assigneeId: getActionAssigneeId(shorthand, eventOwnerId, siteId),
    deadline: getDateFromShorthand(shorthand),
    status: shorthand.status ?? SQDCActionStatus.PLAN,
    fileIds: [],
    SQDCActionTextFields: [],
    SQDCActionNumberFields: [],
    SQDCActionDateFields: [],
    SQDCActionBooleanFields: [],
    SQDCActionCostCenterFields: [],
    SQDCActionTechnicalPlaceFields: [],
    SQDCActionUserFields: [],
    SQDCActionIdeaCategoryFields: [],
    SQDCActionPercentSharingFields: [],
    SQDCActionOutageFields: [],
    SQDCActionErrorCatalogueFields: [],
    SQDCActionProductFamilyFields: [],
  };

  shorthand.FieldValues.forEach((fieldValue) => {
    setActionCustomFieldValue(
      defaultAction,
      fieldValue.fieldId,
      fieldValue.Field,
      resolveUserFieldDefault(
        fieldValue.value,
        fieldValue.Field,
        siteId,
        userFunctionAssignments,
        lastWorkflowUserId,
        eventOwnerId,
      ),
    );
  });

  return defaultAction;
}

export function createDefaultEventForWorkflow(
  shorthand?: SQDCShorthand,
  siteId?: number,
  lastWorkflowUserId?: number,
  userFunctionAssignments: ShorthandUserFunctionAssignments = {},
): ModalEventEditType {
  if (shorthand != null) {
    const defaultEvent = createDefaultEventFromShorthand(
      shorthand,
      siteId,
      userFunctionAssignments,
      lastWorkflowUserId,
    );
    if (shorthand.responsible === SQDCEABShorthandResponsible.LAST_WORKFLOW_USER) {
      defaultEvent.creatorId = lastWorkflowUserId;
    }
    return defaultEvent;
  }
  return {
    creatorId: store.getters["login/isAnonym"] ? undefined : store.getters["login/userId"],
    occurrenceDate: normalizeIsoDateToGranularity(DateTime.now(), DatePickerType.MINUTE),
    eisenhowerPriority: EisenhowerPriority.NO_PRIORITY,
    flagIds: [],
    fileIds: [],
    SQDCEventTextFields: [],
    SQDCEventNumberFields: [],
    SQDCEventDateFields: [],
    SQDCEventBooleanFields: [],
    SQDCEventCostCenterFields: [],
    SQDCEventTechnicalPlaceFields: [],
    SQDCEventUserFields: [],
    SQDCEventIdeaCategoryFields: [],
    SQDCEventPercentSharingFields: [],
    SQDCEventOutageFields: [],
    SQDCEventErrorCatalogueFields: [],
    SQDCEventProductFamilyFields: [],
  };
}

export function createDefaultActionForWorkflow(
  eventOwnerId?: number,
  shorthand?: SQDCShorthand,
  siteId?: number,
  lastWorkflowUserId?: number,
  userFunctionAssignments: ShorthandUserFunctionAssignments = {},
): ModalActionEditType {
  if (shorthand != null) {
    const defaultAction = createDefaultActionFromShorthand(
      shorthand,
      eventOwnerId,
      siteId,
      userFunctionAssignments,
      lastWorkflowUserId,
    );
    if (shorthand.responsible === SQDCEABShorthandResponsible.LAST_WORKFLOW_USER) {
      defaultAction.assigneeId = lastWorkflowUserId;
    }
    return defaultAction;
  }
  return {
    assigneeId: store.getters["login/isAnonym"] ? undefined : store.getters["login/userId"],
    deadline: DateTime.now().toISO(),
    status: SQDCActionStatus.PLAN,
    fileIds: [],
    SQDCActionTextFields: [],
    SQDCActionNumberFields: [],
    SQDCActionDateFields: [],
    SQDCActionBooleanFields: [],
    SQDCActionCostCenterFields: [],
    SQDCActionTechnicalPlaceFields: [],
    SQDCActionUserFields: [],
    SQDCActionIdeaCategoryFields: [],
    SQDCActionPercentSharingFields: [],
    SQDCActionOutageFields: [],
    SQDCActionErrorCatalogueFields: [],
    SQDCActionProductFamilyFields: [],
  };
}

export function eventRequiresUserInput(
  eventType: ModalEventType,
  defaultEvent: ModalEventEditType,
) {
  return (
    !hasValue(defaultEvent.creatorId) ||
    !hasValue(defaultEvent.occurrenceDate) ||
    eventType.SQDCEventActionTypeFields.some(
      (field) => field.required && !hasEventFieldValue(defaultEvent, field.id),
    )
  );
}

export function actionRequiresUserInput(
  actionType: ModalEventType,
  defaultAction: ModalActionEditType,
) {
  return (
    !hasValue(defaultAction.assigneeId) ||
    !hasValue(defaultAction.deadline) ||
    actionType.SQDCEventActionTypeFields.some(
      (field) => field.required && !hasActionFieldValue(defaultAction, field.id),
    )
  );
}

function hasEventFieldValue(event: ModalEventEditType, fieldId: number) {
  if (event.SQDCEventPercentSharingFields.some((field) => field.fieldId === fieldId)) {
    return true;
  }
  if (event.SQDCEventOutageFields.some((field) => field.fieldId === fieldId)) {
    return true;
  }

  return [
    ...event.SQDCEventTextFields,
    ...event.SQDCEventNumberFields,
    ...event.SQDCEventDateFields,
    ...event.SQDCEventBooleanFields,
    ...event.SQDCEventCostCenterFields,
    ...event.SQDCEventTechnicalPlaceFields,
    ...event.SQDCEventUserFields,
    ...event.SQDCEventIdeaCategoryFields,
    ...event.SQDCEventErrorCatalogueFields,
  ].some((field) => field.fieldId === fieldId && hasValue(field.value));
}

function hasActionFieldValue(action: ModalActionEditType, fieldId: number) {
  if (action.SQDCActionPercentSharingFields.some((field) => field.fieldId === fieldId)) {
    return true;
  }
  if (action.SQDCActionOutageFields.some((field) => field.fieldId === fieldId)) {
    return true;
  }

  return [
    ...action.SQDCActionTextFields,
    ...action.SQDCActionNumberFields,
    ...action.SQDCActionDateFields,
    ...action.SQDCActionBooleanFields,
    ...action.SQDCActionCostCenterFields,
    ...action.SQDCActionTechnicalPlaceFields,
    ...action.SQDCActionUserFields,
    ...action.SQDCActionIdeaCategoryFields,
    ...action.SQDCActionErrorCatalogueFields,
  ].some((field) => field.fieldId === fieldId && hasValue(field.value));
}

function getEventCreatorId(shorthand: SQDCShorthand, siteId?: number) {
  switch (shorthand.responsible) {
    case SQDCEABShorthandResponsible.SELF:
      return store.getters["login/isAnonym"] ? undefined : store.getters["login/userId"];
    case SQDCEABShorthandResponsible.STATIC:
      return shorthand.responsibleId;
    case SQDCEABShorthandResponsible.USER_FUNCTION:
      return getFunctionUserId(shorthand, siteId);
    case SQDCEABShorthandResponsible.DYNAMIC:
    case SQDCEABShorthandResponsible.EVENT_OWNER:
    default:
      return undefined;
  }
}

function getActionAssigneeId(shorthand: SQDCShorthand, eventOwnerId?: number, siteId?: number) {
  switch (shorthand.responsible) {
    case SQDCEABShorthandResponsible.SELF:
      return store.getters["login/isAnonym"] ? undefined : store.getters["login/userId"];
    case SQDCEABShorthandResponsible.EVENT_OWNER:
      return eventOwnerId;
    case SQDCEABShorthandResponsible.STATIC:
      return shorthand.responsibleId;
    case SQDCEABShorthandResponsible.USER_FUNCTION:
      return getFunctionUserId(shorthand, siteId);
    case SQDCEABShorthandResponsible.DYNAMIC:
    default:
      return undefined;
  }
}

function getFunctionUserId(shorthand: SQDCShorthand, siteId?: number) {
  return shorthand.ResponsibleFunction?.Assignments.find(
    (assignment) => assignment.siteId === siteId,
  )?.userId;
}

export function getShorthandUserFieldOptionIds(shorthand?: SQDCShorthand) {
  return Object.fromEntries(
    (shorthand?.FieldValues ?? []).flatMap((fieldValue) => {
      const config = getUserFieldDefault(fieldValue.value, fieldValue.Field);
      return config?.responsible === SQDCEABShorthandResponsible.LIST
        ? [[fieldValue.fieldId, config.userOptionIds ?? []] as const]
        : [];
    }),
  );
}

export function getShorthandUserFunctionIds(shorthand?: SQDCShorthand) {
  return [
    ...new Set(
      (shorthand?.FieldValues ?? []).flatMap((fieldValue) => {
        const config = getUserFieldDefault(fieldValue.value, fieldValue.Field);
        return config?.responsible === SQDCEABShorthandResponsible.USER_FUNCTION &&
          config.responsibleFunctionId != null
          ? [config.responsibleFunctionId]
          : [];
      }),
    ),
  ];
}

export function getShorthandUserFieldFunctionOverlayIds(shorthand?: SQDCShorthand) {
  return Object.fromEntries(
    (shorthand?.FieldValues ?? []).flatMap((fieldValue) => {
      const config = getUserFieldDefault(fieldValue.value, fieldValue.Field);
      return config?.responsible === SQDCEABShorthandResponsible.USER_FUNCTION &&
        config.overlayResponsibleRole === true &&
        config.responsibleFunctionId != null
        ? [[fieldValue.fieldId, config.responsibleFunctionId] as const]
        : [];
    }),
  );
}

function resolveUserFieldDefault(
  value: unknown,
  field: SQDCShorthandField,
  siteId: number | undefined,
  userFunctionAssignments: ShorthandUserFunctionAssignments,
  lastWorkflowUserId?: number,
  eventOwnerId?: number,
) {
  const config = getUserFieldDefault(value, field);
  if (config == null) return value;
  switch (config.responsible) {
    case SQDCEABShorthandResponsible.SELF:
      return store.getters["login/isAnonym"] ? undefined : store.getters["login/userId"];
    case SQDCEABShorthandResponsible.STATIC:
      return config.responsibleId;
    case SQDCEABShorthandResponsible.USER_FUNCTION:
      return config.responsibleFunctionId == null
        ? undefined
        : userFunctionAssignments[config.responsibleFunctionId]?.find(
            (assignment) => assignment.siteId === siteId,
          )?.userId;
    case SQDCEABShorthandResponsible.LAST_WORKFLOW_USER:
      return lastWorkflowUserId;
    case SQDCEABShorthandResponsible.EVENT_OWNER:
      return eventOwnerId;
    default:
      return undefined;
  }
}

function getUserFieldDefault(value: unknown, field: SQDCShorthandField) {
  if (
    field.type !== SQDCEventActionTypeFieldType.RELATION ||
    field.relation !== SQDCEventTypeFieldRelation.USER ||
    value == null ||
    typeof value !== "object" ||
    !("responsible" in value)
  ) {
    return undefined;
  }
  return value as ShorthandUserFieldDefault;
}

function getDateFromShorthand(shorthand: SQDCShorthand) {
  switch (shorthand.deadlineType) {
    case SQDCEABShorthandDeadline.PRESENT:
      return DateTime.now().toISO();
    case SQDCEABShorthandDeadline.FUTURE:
      return DateTime.now()
        .plus({ days: shorthand.deadlineValue ?? 0 })
        .toISO();
    case SQDCEABShorthandDeadline.NEXT_WEEKDAY:
      return getNextWeekday(shorthand.deadlineWeekday ?? 1).toISO();
    case SQDCEABShorthandDeadline.STATIC:
      return shorthand.deadlineDate;
    case SQDCEABShorthandDeadline.DYNAMIC:
    default:
      return undefined;
  }
}

function setEventCustomFieldValue(
  event: ModalEventEditType,
  fieldId: number,
  field: SQDCShorthandField,
  value: unknown,
) {
  if (value == null || value === "" || (Array.isArray(value) && value.length === 0)) {
    return;
  }

  switch (field.type) {
    case SQDCEventActionTypeFieldType.TEXT:
    case SQDCEventActionTypeFieldType.TEXTAREA:
    case SQDCEventActionTypeFieldType.ENUM:
    case SQDCEventActionTypeFieldType.VETO:
    case SQDCEventActionTypeFieldType.RECOMMENDATION:
      {
        const row = { fieldId, value: normalizeTextFieldValue(value) };
        event.SQDCEventTextFields.push(row as ModalEventEditType["SQDCEventTextFields"][number]);
      }
      return;
    case SQDCEventActionTypeFieldType.COMMENT:
      return;
    case SQDCEventActionTypeFieldType.NUMBER:
      event.SQDCEventNumberFields.push({
        fieldId,
        value,
      } as ModalEventEditType["SQDCEventNumberFields"][number]);
      return;
    case SQDCEventActionTypeFieldType.DATE:
    case SQDCEventActionTypeFieldType.DATERANGE:
      setDateFieldValue(event.SQDCEventDateFields, fieldId, field, value);
      return;
    case SQDCEventActionTypeFieldType.REPAIR_WINDOW:
      setRepairWindowFieldValue(
        event.SQDCEventTextFields,
        event.SQDCEventDateFields,
        fieldId,
        field,
        value,
      );
      return;
    case SQDCEventActionTypeFieldType.BOOLEAN:
      event.SQDCEventBooleanFields.push({
        fieldId,
        value,
      } as ModalEventEditType["SQDCEventBooleanFields"][number]);
      return;
    case SQDCEventActionTypeFieldType.PERCENT_SHARING:
      event.SQDCEventPercentSharingFields.push(...getPercentSharingRows(fieldId, value));
      return;
    case SQDCEventActionTypeFieldType.OUTAGE:
      setOutageFieldValue(event.SQDCEventOutageFields, fieldId, value);
      return;
    case SQDCEventActionTypeFieldType.RELATION:
      setEventRelationFieldValue(event, field, { fieldId, value });
  }
}

function normalizeTextFieldValue(value: unknown): string | string[] {
  if (Array.isArray(value)) {
    return value.flatMap((entry) => {
      const normalizedEntry = normalizeTextFieldValue(entry);
      return Array.isArray(normalizedEntry) ? normalizedEntry : [normalizedEntry];
    });
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (value != null && typeof value === "object") {
    for (const key of ["value", "text", "label"]) {
      if (!(key in value)) {
        continue;
      }
      const nestedValue = (value as Record<string, unknown>)[key];
      if (
        typeof nestedValue === "string" ||
        typeof nestedValue === "number" ||
        typeof nestedValue === "boolean"
      ) {
        return String(nestedValue);
      }
    }

    return JSON.stringify(value);
  }
  return "";
}

function setOutageFieldValue(
  target:
    | ModalEventEditType["SQDCEventOutageFields"]
    | ModalActionEditType["SQDCActionOutageFields"],
  fieldId: number,
  value: unknown,
) {
  if (value == null || typeof value !== "object" || !("hasOutage" in value)) {
    return;
  }

  const outageValue = value as Record<string, unknown>;
  target.push({
    fieldId,
    hasOutage: Boolean(outageValue.hasOutage),
    startDate:
      typeof outageValue.startDate === "string" || outageValue.startDate == null
        ? outageValue.startDate
        : undefined,
    endDate:
      typeof outageValue.endDate === "string" || outageValue.endDate == null
        ? outageValue.endDate
        : undefined,
  });
}

function setEventRelationFieldValue(
  event: ModalEventEditType,
  field: SQDCShorthandField,
  row: { fieldId: number; value: unknown },
) {
  switch (field.relation) {
    case SQDCEventTypeFieldRelation.COST_CENTER:
      event.SQDCEventCostCenterFields.push(
        row as ModalEventEditType["SQDCEventCostCenterFields"][number],
      );
      return;
    case SQDCEventTypeFieldRelation.TECHNICAL_PLACE:
      event.SQDCEventTechnicalPlaceFields.push(
        row as ModalEventEditType["SQDCEventTechnicalPlaceFields"][number],
      );
      return;
    case SQDCEventTypeFieldRelation.USER:
      event.SQDCEventUserFields.push(row as ModalEventEditType["SQDCEventUserFields"][number]);
      return;
    case SQDCEventTypeFieldRelation.IDEA_CATEGORY:
      event.SQDCEventIdeaCategoryFields.push(
        row as ModalEventEditType["SQDCEventIdeaCategoryFields"][number],
      );
      return;
    case SQDCEventTypeFieldRelation.ERROR_CATALOGUE:
      event.SQDCEventErrorCatalogueFields.push(...getNumericRelationRows(row));
  }
}

function setActionCustomFieldValue(
  action: ModalActionEditType,
  fieldId: number,
  field: SQDCShorthandField,
  value: unknown,
) {
  if (value == null || value === "" || (Array.isArray(value) && value.length === 0)) {
    return;
  }

  switch (field.type) {
    case SQDCEventActionTypeFieldType.TEXT:
    case SQDCEventActionTypeFieldType.TEXTAREA:
    case SQDCEventActionTypeFieldType.ENUM:
    case SQDCEventActionTypeFieldType.VETO:
    case SQDCEventActionTypeFieldType.RECOMMENDATION:
      {
        const row = { fieldId, value: normalizeTextFieldValue(value) };
        action.SQDCActionTextFields.push(
          row as ModalActionEditType["SQDCActionTextFields"][number],
        );
      }
      return;
    case SQDCEventActionTypeFieldType.COMMENT:
      return;
    case SQDCEventActionTypeFieldType.NUMBER:
      action.SQDCActionNumberFields.push({
        fieldId,
        value,
      } as ModalActionEditType["SQDCActionNumberFields"][number]);
      return;
    case SQDCEventActionTypeFieldType.DATE:
    case SQDCEventActionTypeFieldType.DATERANGE:
      setDateFieldValue(action.SQDCActionDateFields, fieldId, field, value);
      return;
    case SQDCEventActionTypeFieldType.REPAIR_WINDOW:
      setRepairWindowFieldValue(
        action.SQDCActionTextFields,
        action.SQDCActionDateFields,
        fieldId,
        field,
        value,
      );
      return;
    case SQDCEventActionTypeFieldType.BOOLEAN:
      action.SQDCActionBooleanFields.push({
        fieldId,
        value,
      } as ModalActionEditType["SQDCActionBooleanFields"][number]);
      return;
    case SQDCEventActionTypeFieldType.PERCENT_SHARING:
      action.SQDCActionPercentSharingFields.push(...getPercentSharingRows(fieldId, value));
      return;
    case SQDCEventActionTypeFieldType.OUTAGE:
      setOutageFieldValue(action.SQDCActionOutageFields, fieldId, value);
      return;
    case SQDCEventActionTypeFieldType.RELATION:
      setActionRelationFieldValue(action, field, { fieldId, value });
  }
}

function setDateFieldValue(
  target: ModalEventEditType["SQDCEventDateFields"] | ModalActionEditType["SQDCActionDateFields"],
  fieldId: number,
  field: SQDCShorthandField,
  value: unknown,
) {
  const resolvedValue = resolveDateFieldValue(value, field.dateGranularity);
  if (!hasValue(resolvedValue)) {
    return;
  }
  target.push({ fieldId, value: resolvedValue } as never);
}

function setRepairWindowFieldValue(
  textTarget:
    | ModalEventEditType["SQDCEventTextFields"]
    | ModalActionEditType["SQDCActionTextFields"],
  dateTarget:
    | ModalEventEditType["SQDCEventDateFields"]
    | ModalActionEditType["SQDCActionDateFields"],
  fieldId: number,
  field: SQDCShorthandField,
  value: unknown,
) {
  const repairWindow =
    value != null && typeof value === "object" ? (value as Record<string, unknown>) : undefined;
  const enumValue =
    repairWindow == null
      ? normalizeTextFieldValue(value)
      : normalizeTextFieldValue(repairWindow.enumValue);
  if (hasValue(enumValue) && !Array.isArray(enumValue)) {
    textTarget.push({ fieldId, value: enumValue } as never);
  }
  const dateValue = repairWindow?.dateValue;
  const resolvedDate = resolveDateFieldValue(dateValue, field.dateGranularity);
  if (hasValue(resolvedDate)) {
    dateTarget.push({ fieldId, value: resolvedDate } as never);
  }
}

function resolveDateFieldValue(value: unknown, granularity: DatePickerType | null | undefined) {
  if (!isShorthandDateFieldDefault(value)) {
    if (Array.isArray(value)) {
      return value
        .map((entry, index) =>
          normalizeIsoDateToGranularity(
            typeof entry === "string" ? entry : undefined,
            granularity,
            index === 1 ? "end" : "start",
          ),
        )
        .filter((entry): entry is string => entry != null);
    }
    return normalizeIsoDateToGranularity(
      typeof value === "string" ? value : undefined,
      granularity,
    );
  }

  switch (value.mode) {
    case SQDCEABShorthandDeadline.PRESENT:
      return normalizeIsoDateToGranularity(DateTime.now(), granularity);
    case SQDCEABShorthandDeadline.FUTURE:
      return normalizeIsoDateToGranularity(
        DateTime.now().plus({ days: value.value ?? 0 }),
        granularity,
      );
    case SQDCEABShorthandDeadline.NEXT_WEEKDAY:
      return normalizeIsoDateToGranularity(getNextWeekday(value.weekday ?? 1), granularity);
    case SQDCEABShorthandDeadline.STATIC:
      return normalizeIsoDateToGranularity(value.date, granularity);
    case SQDCEABShorthandDeadline.DYNAMIC:
    default:
      return undefined;
  }
}

function isShorthandDateFieldDefault(value: unknown): value is ShorthandDateFieldDefault {
  return typeof value === "object" && value != null && "mode" in value;
}

function getNextWeekday(weekday: number) {
  const today = DateTime.now();
  const daysUntilWeekday = (weekday - today.weekday + 7) % 7 || 7;
  return today.plus({ days: daysUntilWeekday });
}

function getPercentSharingRows(fieldId: number, value: unknown) {
  if (value == null || typeof value !== "object" || !("participants" in value)) {
    return [];
  }

  return Array.isArray(value.participants)
    ? value.participants
        .filter(
          (participant): participant is { userId: number; percentage: number } =>
            participant != null &&
            typeof participant === "object" &&
            typeof participant.userId === "number" &&
            typeof participant.percentage === "number",
        )
        .map((participant) => ({ fieldId, ...participant }))
    : [];
}

function setActionRelationFieldValue(
  action: ModalActionEditType,
  field: SQDCShorthandField,
  row: { fieldId: number; value: unknown },
) {
  switch (field.relation) {
    case SQDCEventTypeFieldRelation.COST_CENTER:
      action.SQDCActionCostCenterFields.push(
        row as ModalActionEditType["SQDCActionCostCenterFields"][number],
      );
      return;
    case SQDCEventTypeFieldRelation.TECHNICAL_PLACE:
      action.SQDCActionTechnicalPlaceFields.push(
        row as ModalActionEditType["SQDCActionTechnicalPlaceFields"][number],
      );
      return;
    case SQDCEventTypeFieldRelation.USER:
      action.SQDCActionUserFields.push(row as ModalActionEditType["SQDCActionUserFields"][number]);
      return;
    case SQDCEventTypeFieldRelation.IDEA_CATEGORY:
      action.SQDCActionIdeaCategoryFields.push(
        row as ModalActionEditType["SQDCActionIdeaCategoryFields"][number],
      );
      return;
    case SQDCEventTypeFieldRelation.ERROR_CATALOGUE:
      action.SQDCActionErrorCatalogueFields.push(...getNumericRelationRows(row));
  }
}

function getNumericRelationRows(row: { fieldId: number; value: unknown }) {
  const values = Array.isArray(row.value) ? row.value : [row.value];
  return values.flatMap((value, index) =>
    typeof value === "number" ? [{ fieldId: row.fieldId, value, index }] : [],
  );
}
